import * as THREE from "three";

import Experience from "../Experience";
import { toThreeVector } from "../Utils/aeCoords";
import { installCardFade } from "../Utils/cardFade";
import { evalLayerState } from "../Utils/layerAnim";

// Drawn at 2x and downsampled by the texture filter — canvas text at 1:1
// shimmers once the card starts moving in perspective (reference: TEXT_SS).
const TEXT_SUPERSAMPLE = 2;

// Real font, bundled as @font-face in style.css from public/hyundai-tco/
// fonts/ — matches scene.json's own text.family exactly.
const FONT_FAMILY = "Hyundai Sans Head";

const _tl = new THREE.Vector3();
const _tr = new THREE.Vector3();
const _bl = new THREE.Vector3();
const _ex = new THREE.Vector3();
const _down = new THREE.Vector3();
const _exN = new THREE.Vector3();
const _eyN = new THREE.Vector3();
const _ez = new THREE.Vector3();
const _centre = new THREE.Vector3();

// "How it started" — rasterizes the preset's text onto a canvas and shows
// it as a corner-pinned card, exact port of the reference's drawTextCard +
// applyFrame text-layer branch. No CSS3DRenderer: staying on a WebGL plane
// keeps this layer inside the same corner-pin/parallax basis and the same
// grading/FXAA/vignette post-processing chain as the plate and turntable —
// a DOM overlay would sit outside all of that (see conversation).
export default class TextCard {
  constructor() {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.sceneData = this.experience.sceneData;

    this.layer = this.sceneData.getLayer("How it started");
    this.config = this.sceneData.preset.layers.find((l) => l.name === "How it started");
    this.anims = this.config.anims || [];

    // Current eased visibility, 0 (hidden) .. 1 (shown) — ramps toward
    // evalLayerState()'s target at 1/fadeDuration per second, exact port of
    // the reference's stepAnim easing (not a one-shot tween, so it stays
    // correct if the playhead ever moves backward through a hide/show
    // boundary, not just forward through this one on first arrival).
    this.visibility = 0;

    this.setConfig();
    this.setAnchorFrame();
    this.createMesh();

    // The mesh exists (and its material) before the canvas is drawn — the
    // @font-face file may still be downloading at construction time, and
    // rasterizing before it's ready would silently fall back to a system
    // font baked permanently into the texture. document.fonts.load() both
    // triggers the fetch (if not already in flight from the CSS rule) and
    // resolves once it's actually usable for measurement/fillText.
    //
    // Exposed as this.ready — World/Experience await it (alongside
    // CouplesRotation's own async init) before calling renderer.compile(),
    // so the map/needsUpdate flip below (which forces a real shader
    // program recompile, since USE_MAP wasn't even defined at the first,
    // mapless compile) happens on an invisible mesh well before the first
    // frame this card is ever shown, instead of stalling that reveal frame.
    const weight = /bold/i.test(this.text.style || "") ? "700" : "400";
    this.ready = document.fonts
      .load(`${weight} ${this.text.size * TEXT_SUPERSAMPLE}px "${FONT_FAMILY}"`)
      .catch(() => {})
      .then(() => {
        this.buildTextCanvas();
        this.material.map = this.texture;
        this.material.needsUpdate = true;
        this.updateTransform();
      });
  }

  // Ground-truthed by actually loading preset.json into the real reference
  // tool (headless) and screenshotting frame 140 — settles the scalePct AND
  // position arguments definitively, not by further guessing:
  //  - preset.json's txt* fields (the live editor state) ARE what the
  //    reference renders, confirmed pixel-for-pixel against that
  //    screenshot: full scalePct 20% applied, matching size exactly.
  //  - txtContent's long space-runs ("How it<27 spaces>\n<22 spaces>started")
  //    are NOT editor padding to strip — under textAlign:"center", each
  //    line's own measured width includes its trailing/leading spaces, so
  //    centering shifts each line's VISIBLE glyphs by a different amount:
  //    "How it"+trailing spaces centers glyphs left of canvas-centre,
  //    <leading spaces>+"started" centers glyphs right of it. This is the
  //    exact staggered "How it" / "started" layout seen in the reference
  //    screenshot — a deliberate authoring trick via whitespace, not a bug.
  //    My earlier per-line .trim() removed the very thing producing it.
  setConfig() {
    const c = this.config;
    this.text = {
      text: c.txtContent,
      family: this.layer.text.family,
      style: this.layer.text.style,
      size: Number(c.txtSize),
      leading: Number(c.txtLeading),
      tracking: Number(c.txtTracking),
      justify: c.txtJustify,
      color: this.layer.text.color,
    };
    // Raw, not /100 — matches the reference's shiftX/Y/Z (see turntable's
    // tt_shiftX for the same convention verified elsewhere in this port).
    this.shiftX = Number(c.shiftX);
    this.shiftY = Number(c.shiftY);
    this.shiftZ = Number(c.shiftZ);
    this.scale = Number(c.scalePct) / 100;
    this.fadeDuration = Number(c.dur) || 1;
    // UI slider is an integer 0-60, stored as /1000 UV units (reference:
    // range(...).dataset.k='blurAmt' -> L.blur = value/1000).
    this.blurAmt = Number(c.blurAmt) / 1000;
  }

  // Corner-pin basis from the layer's single baked frame — same ex/down/ez
  // construction as CouplesRotation's Floor anchor, generalized to any
  // layer (not Floor-specific).
  setAnchorFrame() {
    const compW = this.sceneData.scene.comp.width;
    const compH = this.sceneData.scene.comp.height;
    const frame = this.layer.frames[0];

    _tl.copy(toThreeVector(frame.tl, compW, compH));
    _tr.copy(toThreeVector(frame.tr, compW, compH));
    _bl.copy(toThreeVector(frame.bl, compW, compH));

    _ex.subVectors(_tr, _tl);
    _down.subVectors(_bl, _tl);
    _ez.crossVectors(_ex, _down.clone().negate()).normalize();
    _exN.copy(_ex).normalize();
    _eyN.copy(_down).normalize().negate();

    this.tl = _tl.clone();
    this.ex = _ex.clone();
    this.exN = _exN.clone();
    this.eyN = _eyN.clone();
    this.ez = _ez.clone();
  }

  // Rasterizes T.content onto a canvas sized to the measured text (not the
  // AE bounds — the box is only ever as big as this specific string at
  // this specific size, so an edit never crops). Exact port of the
  // reference's drawTextCard.
  buildTextCanvas() {
    const T = this.text;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const px = T.size * TEXT_SUPERSAMPLE;
    const weight = /bold/i.test(T.style || "") ? "700 " : "";
    const style = /italic/i.test(T.style || "") ? "italic " : "";
    const font = `${style}${weight}${px}px "${FONT_FAMILY}"`;
    const track = (T.tracking / 1000) * px;
    const lines = String(T.text).replace(/\r\n?/g, "\n").split("\n");

    ctx.font = font;
    if ("letterSpacing" in ctx) ctx.letterSpacing = `${track}px`;
    let maxW = 2;
    for (const line of lines) maxW = Math.max(maxW, ctx.measureText(line).width);

    const lead = T.leading * TEXT_SUPERSAMPLE;
    const ascent = px * 0.8;
    const descent = px * 0.3;
    const padX = Math.ceil(px * 0.3);
    const padY = Math.ceil(px * 0.3);
    const width = Math.ceil(maxW) + padX * 2;
    const height = Math.ceil(ascent + descent + lead * (lines.length - 1)) + padY * 2;

    canvas.width = width;
    canvas.height = height;
    ctx.font = font;
    if ("letterSpacing" in ctx) ctx.letterSpacing = `${track}px`;
    ctx.fillStyle = `rgb(${T.color.map((c) => Math.round(c * 255)).join(",")})`;
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = T.justify;

    const x = T.justify === "center" ? width / 2 : T.justify === "right" ? width - padX : padX;
    let y = padY + ascent;
    for (const line of lines) {
      ctx.fillText(line, x, y);
      y += lead;
    }

    // Card-space size (not canvas pixels) — used by createMesh to size the
    // quad, and grows/shrinks with edits the same way AE text would.
    this.textWidth = width / TEXT_SUPERSAMPLE;
    this.textHeight = height / TEXT_SUPERSAMPLE;

    this.texture = new THREE.CanvasTexture(canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.generateMipmaps = false;
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
  }

  // Mesh exists immediately with no map — buildTextCanvas() (deferred until
  // the font is ready, see constructor) sizes it for the first time via
  // updateTransform() once the texture actually exists.
  createMesh() {
    this.material = new THREE.MeshBasicMaterial({
      transparent: true,
      depthWrite: false,
    });
    this.fade = installCardFade(this.material, this.blurAmt);

    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.material);
    this.mesh.visible = false;
    this.scene.add(this.mesh);
  }

  // Positions/sizes the quad from the measured text box, growing about the
  // anchor point the justification implies (left-set keeps its left edge
  // and grows right, centred grows both ways, right-set keeps its right
  // edge — matching drawTextCard's own anchor rule) rather than the baked
  // AE corners directly, since the real font's own metrics can still differ
  // slightly from the original authoring environment's.
  updateTransform() {
    const w = this.textWidth * this.scale;
    const h = this.textHeight * this.scale;
    const anchorShift = this.text.justify === "center" ? 0.5 : this.text.justify === "right" ? 1 : 0;

    const ex2 = this.exN.clone().multiplyScalar(w);
    const ey2 = this.eyN.clone().multiplyScalar(h);
    _centre
      .copy(this.tl)
      .addScaledVector(this.ex, anchorShift)
      .addScaledVector(this.exN, w * (0.5 - anchorShift))
      .addScaledVector(this.eyN, -h * 0.5)
      .addScaledVector(this.exN, this.shiftX)
      .addScaledVector(this.eyN, this.shiftY)
      .addScaledVector(this.ez, this.shiftZ); 

    this.mesh.matrix.makeBasis(ex2, ey2, this.ez).setPosition(_centre);
    this.mesh.matrixAutoUpdate = false;
    this.mesh.matrixWorldNeedsUpdate = true;
  }

  // Eases visibility toward the target evalLayerState() implies at the
  // current playhead (reference: stepAnim) — linear ramp at 1/fadeDuration
  // per second, not an easing curve, matching the reference exactly (the
  // eased *feel* comes from the blur/opacity combination in cardFade, not
  // from the ramp itself).
  update(dt, playheadFrame) {
    const target = evalLayerState(this.anims, this.sceneData.clips, playheadFrame);
    if (this.visibility !== target) {
      const step = dt / Math.max(0.02, this.fadeDuration);
      this.visibility =
        target > this.visibility
          ? Math.min(target, this.visibility + step)
          : Math.max(target, this.visibility - step);
    }

    this.fade.setVisibility(this.visibility);
    this.mesh.visible = this.visibility > 0.001;
  }
}
